import { Controller } from "@hotwired/stimulus";
import {
  get,
  parseRequestOptionsFromJSON,
} from "@github/webauthn-json/browser-ponyfill";
import { jsonHeaders } from "./passkey_csrf";

// Connects to data-controller="passkey-authentication"
// Values: optionsUrl, callbackUrl   Targets: status, button, email
export default class extends Controller {
  static values = { optionsUrl: String, callbackUrl: String };
  static targets = ["status", "button", "email"];

  async authenticate(event) {
    event.preventDefault();
    if (!this.supported()) {
      this.setStatus("This device does not support passkeys.", true);
      return;
    }

    // Stop the autofill/conditional ceremony before starting a modal one.
    window.dispatchEvent(new CustomEvent("passkey:manual-start"));

    this.disable(true);
    this.setStatus("Follow your device prompt…");

    try {
      // Scope the ceremony to the email already entered, so the device prompts
      // for that account's passkey instead of listing every resident one.
      const email = this.hasEmailTarget ? this.emailTarget.value.trim() : "";
      const { options, ceremony_id } = await this.fetchJson(
        this.optionsUrlValue,
        email ? { email } : {},
      );
      const credential = await get(
        parseRequestOptionsFromJSON({ publicKey: options }),
      );

      const result = await this.fetchJson(this.callbackUrlValue, {
        publicKeyCredential: credential,
        ceremony_id,
      });

      if (result.redirect_to) {
        Turbo.visit(result.redirect_to);
      } else {
        this.setStatus("Could not sign in.", true);
        this.disable(false);
      }
    } catch (err) {
      this.setStatus(this.friendlyError(err), true);
      this.disable(false);
    }
  }

  async fetchJson(url, body) {
    const res = await fetch(url, {
      method: "POST",
      headers: jsonHeaders(),
      credentials: "same-origin",
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const msg = data.errors
        ? Object.values(data.errors).flat().join(", ")
        : "Authentication failed.";
      throw new Error(msg);
    }
    return data;
  }

  supported() {
    return (
      window.PublicKeyCredential &&
      navigator.credentials &&
      navigator.credentials.get
    );
  }

  friendlyError(err) {
    if (err.name === "NotAllowedError")
      return "Request was cancelled or timed out.";
    return err.message || "Could not sign in with a passkey.";
  }

  setStatus(msg, isError = false) {
    if (!this.hasStatusTarget) return;
    this.statusTarget.textContent = msg;
    this.statusTarget.classList.toggle("text-red-600", isError);
  }

  disable(state) {
    if (this.hasButtonTarget) this.buttonTarget.disabled = state;
  }
}
